import { useCallback, type MutableRefObject } from 'react';
import { api, type ChatMessageResponse } from '@/lib/api';

interface MessagesLike {
  messages: ChatMessageResponse[];
  updateMessage: (messageId: string, patch: Partial<ChatMessageResponse>) => void;
}

/**
 * Toggle-a-reaction, shared by every surface that renders a message list.
 *
 * Lives in one place because the optimistic update is fiddly in three distinct
 * ways — removing your own reaction has to drop the row when the count hits
 * zero, adding to an existing emoji has to append your id, and a brand-new
 * emoji has to create the row — and a second copy would inevitably drift from
 * this one. The revert path restores the ORIGINAL reactions array rather than
 * re-deriving it, so a failed request can't leave a half-applied count behind.
 *
 * Takes a ref (not a value) so the callback stays stable while messages change.
 */
export function useMessageReactions(
  messagesRef: MutableRefObject<MessagesLike>,
  userId: string | null,
) {
  return useCallback(
    async (messageId: string, emoji: string) => {
      const msg = messagesRef.current.messages.find((m) => m.messageId === messageId);
      if (!msg || !userId) return;

      const existing = msg.reactions?.find((r) => r.emoji === emoji);
      const alreadyReacted = existing?.userIds.includes(userId);

      messagesRef.current.updateMessage(messageId, {
        reactions: alreadyReacted
          ? (msg.reactions ?? [])
              .map((r) =>
                r.emoji === emoji
                  ? {
                      ...r,
                      count: r.count - 1,
                      userIds: r.userIds.filter((id) => id !== userId),
                    }
                  : r,
              )
              .filter((r) => r.count > 0)
          : existing
            ? (msg.reactions ?? []).map((r) =>
                r.emoji === emoji
                  ? { ...r, count: r.count + 1, userIds: [...r.userIds, userId] }
                  : r,
              )
            : [...(msg.reactions ?? []), { emoji, count: 1, userIds: [userId] }],
      });

      try {
        if (alreadyReacted) {
          await api.removeChatReaction(messageId, emoji);
        } else {
          await api.addChatReaction(messageId, emoji);
        }
      } catch {
        // Revert to the pre-click array on failure.
        messagesRef.current.updateMessage(messageId, { reactions: msg.reactions });
      }
    },
    [messagesRef, userId],
  );
}
